import React from 'react';
import { FrameBinding } from '../clips/sections/referenceFrames';

interface SceneFrameHandshakeBannerProps {
  /** The binding "Use new opening frame" would write, or null to hide the button. */
  openingBinding: FrameBinding | null;
  /** Same for the closing frame. Null whenever the model has no closing frame. */
  closingBinding: FrameBinding | null;
  /** The offered binding is already the one the video will generate from. */
  openingBound: boolean;
  closingBound: boolean;
  onUseOpening: (binding: FrameBinding) => void;
  onUseClosing: (binding: FrameBinding) => void;
}

const glowButton =
  'w-full py-1.5 px-3 font-mono text-[11px] uppercase tracking-[0.15em] text-amber-200 border border-amber-400/60 bg-amber-400/10 hover:bg-amber-400/20 shadow-[0_0_10px_0_rgba(251,191,36,0.35)] animate-pulse transition-colors';

const inUse = (label: string) => (
  <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">{label} ✓</p>
);

/**
 * The handshake half of the scene dialog: a frame was regenerated while this
 * was open, and the video is still bound to the take it replaced.
 *
 * Binding the NEW still is deliberately an explicit act rather than an
 * automatic rewire — the slot may legitimately have been pointed elsewhere —
 * so this is a button, and it turns into a "✓ in use" line once pressed.
 * Renders nothing when neither frame has a fresh take to offer.
 */
const SceneFrameHandshakeBanner: React.FC<SceneFrameHandshakeBannerProps> = ({
  openingBinding,
  closingBinding,
  openingBound,
  closingBound,
  onUseOpening,
  onUseClosing,
}) => {
  if (!openingBinding && !closingBinding) return null;

  return (
    <div className="space-y-2 border border-amber-400/25 bg-amber-400/5 p-3">
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-amber-200/70">
        New frame generated
      </p>
      {openingBinding &&
        (openingBound ? (
          inUse('New opening frame in use')
        ) : (
          <button
            type="button"
            data-testid="use-new-opening-frame"
            className={glowButton}
            onClick={() => onUseOpening(openingBinding)}
          >
            Use new opening frame
          </button>
        ))}
      {closingBinding &&
        (closingBound ? (
          inUse('New closing frame in use')
        ) : (
          <button
            type="button"
            data-testid="use-new-closing-frame"
            className={glowButton}
            onClick={() => onUseClosing(closingBinding)}
          >
            Use new closing frame
          </button>
        ))}
    </div>
  );
};

export default SceneFrameHandshakeBanner;
